/**
 * @file repContactCard.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description What a rep's phone card says about one customer (Search
 *              Contacts → RepContactCard). Cam, 29 Oct 2026: "Checking the
 *              rep mobile view: can the contact cards look more like the
 *              order cards etc. please? … make uniform."
 *
 * The customers list on the phone was the desktop table restyled by CSS —
 * "They owe", "We owe", "Credit limit" and "Groups" as label / value rows,
 * a dash for each empty one, the account number in a grey blob and ACTIVE in
 * a badge on every card. It is now a card built like the order card: the
 * customer with its account badge, where it is, how much it owes us in words
 * and figures, and Call / Email along the foot.
 *
 * What owes means here is the desktop's "They owe" cell, the same columns:
 * past due (`they_owe_aged`, an invoice whose due date has gone) and not yet
 * due (`they_owe_current`). Groups are left off, as the customer window on
 * the phone leaves them off — they are the office's (invoice posting, delivery
 * accounts), not something a rep acts on.
 *
 * Pure: no React, no fetch.
 */

import { moneyText } from './repDocCards';
import { phoneTelHref, phoneDisplay } from './phoneNumber';

const PENNY = 0.005;
const text = (v) => String(v == null ? '' : v).trim();
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "Westcott Farm · Tiverton · EX16 7EW" — delivery address, else invoice. */
export function contactPlace(contact = {}) {
  const side = text(contact.delivery_line1) || text(contact.delivery_postcode) ? 'delivery_' : 'invoice_';
  const parts = [
    text(contact[`${side}line1`]),
    text(contact[`${side}city`]) || text(contact[`${side}region`]),
    text(contact[`${side}postcode`])
  ].filter(Boolean);
  // A farm whose first line IS its town ("Tiverton · Tiverton") once.
  const seen = new Set();
  return parts.filter(p => {
    const key = p.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).join(' · ');
}

/**
 * The number Call dials: the mobile first (a rep calls a farmer in the
 * yard), else the phone. `{ label, display, href }`, or null with neither.
 */
export function contactPhone(contact = {}) {
  const pick = [
    ['Mobile', contact.mobile, contact.mobile_country_code, contact.mobile_area_code],
    ['Phone', contact.phone, contact.phone_country_code, contact.phone_area_code]
  ].find(([, number]) => text(number));
  if (!pick) return null;
  const [label, number, country, area] = pick;
  const href = phoneTelHref(text(number), text(country), text(area));
  if (!href) return null;
  return { label, display: phoneDisplay(text(number), text(country), text(area), ''), href };
}

/** `{ address, href }` for Email, or null when there is no address. */
export function contactEmail(contact = {}) {
  const address = text(contact.email);
  if (!address || !address.includes('@')) return null;
  return { address, href: `mailto:${address}` };
}

/**
 * One customer as a card.
 *
 * Status, furthest first: archived; overdue (anything past its due date);
 * owing but not yet due; nothing owed. The figure beside it is everything
 * owed, with how much of it is overdue under it when only part is.
 */
export function repContactCard(contact = {}) {
  const archived = text(contact.contact_status).toUpperCase() === 'ARCHIVED';
  const aged = Math.max(0, num(contact.they_owe_aged));
  const current = Math.max(0, num(contact.they_owe_current));
  const owed = aged + current;

  let chip;
  if (archived) chip = { label: 'Archived', tone: 'muted' };
  else if (aged > PENNY) chip = { label: 'Overdue', tone: 'error' };
  else if (owed > PENNY) chip = { label: 'Not yet due', tone: 'info' };
  else chip = { label: 'Nothing owed', tone: 'success' };

  const extraChips = [];
  if (contact.contracted) extraChips.push({ key: 'contracted', label: 'Contracted', tone: 'info' });
  const weOwe = Math.max(0, num(contact.we_owe_aged)) + Math.max(0, num(contact.we_owe_current));
  if (weOwe > PENNY) extraChips.push({ key: 'we-owe', label: `We owe ${moneyText(weOwe)}`, tone: 'muted' });

  // A supplier or a contact on both sides says so; a plain customer — every
  // card a rep sees, almost — does not, or every card would carry the word.
  const type = text(contact.contact_type).toUpperCase();
  const kind = type === 'BOTH' ? 'Customer & supplier' : type === 'SUPPLIER' ? 'Supplier' : '';

  const flags = [];
  if (contact.isParent || contact.is_parent) flags.push({ key: 'parent', label: 'Has linked accounts' });
  const parentNo = text(contact.parentAccountNo || contact.parent_account_no);
  if (parentNo) flags.push({ key: 'child', label: `Linked to ${parentNo}` });

  const credit = num(contact.credit_limit);

  return {
    badge: text(contact.account_no),
    kind,
    title: text(contact.company_name) || 'No name',
    place: contactPlace(contact),
    chip,
    extraChips,
    total: owed > PENNY ? moneyText(owed) : '',
    totalSub: aged > PENNY && current > PENNY ? `${moneyText(aged)} overdue` : '',
    creditText: credit > PENNY ? `Credit limit ${moneyText(credit)}` : '',
    flags,
    archived,
    phone: contactPhone(contact),
    email: contactEmail(contact)
  };
}
